import React, { useState } from 'react';
import { makeStyles } from 'react-scale/components/design-system/styles';
import { XButton } from './XButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { TaskType } from '@scale/shared-types';
import ExampleViewer from './ExampleViewer';
import { useSlate } from 'slate-react';
import { useInstructionTaskData } from './hooks';

interface ExampleLabelPreviewProps {
  taskId: string;
  isGoodExample: boolean;
  location: $FIXME;
  caption?: string;
  index: number;
  readOnly: boolean;
  onDelete: Function;
  hasOpenedExample?: (taskId: string, isGoodExample: boolean) => void;
  projectTemplateId?: string;
  isMigration?: boolean;
  onUpdate?: $FIXME;
}

const getAttachmentInfo = (taskData: any) => {
  if (!taskData) {
    return { attachmentType: null, attachment: null };
  }
  let attachmentType, attachment;
  const taskType = taskData.type;

  if (taskType === TaskType.TextCollection) {
    attachmentType = taskData.params.attachments[0].type;
    attachment = taskData.params.attachments[0].content;
  } else if (taskType === TaskType.NamedEntityRecognition) {
    attachmentType = 'text';
    attachment = taskData.params.text;
  } else if (taskType === TaskType.DocumentTranscription) {
    attachmentType = 'image';
    attachment = taskData.params.attachments[0];
  } else {
    attachmentType = taskData.params.attachment_type;
    attachment = taskData.params.attachment;
  }

  return { attachmentType, attachment };
};

/*
 * render a preview of an example task
 * when user clicks on it, open modal with full example shown
 */
export const ExampleLabelPreview: React.FC<ExampleLabelPreviewProps> = ({
  taskId,
  isGoodExample,
  location,
  index,
  caption,
  readOnly,
  onDelete,
  hasOpenedExample,
  projectTemplateId,
  onUpdate: handleUpdateExample,
}) => {
  const classes = useStyles();
  const editor = useSlate();

  const [exampleViewerOpen, setExampleViewerOpen] = useState(false);
  const [curSelection, setCurSelection] = useState<any>(null);

  const { isLoading, data: taskData } = useInstructionTaskData(taskId, readOnly, projectTemplateId);

  if (isLoading) {
    return <div>Loading...</div>;
  }

  const borderColor = isGoodExample ? 'green' : 'red';
  const { attachmentType, attachment } = getAttachmentInfo(taskData);

  const renderLabel = (index: number) => {
    const deleteBtn = !readOnly && <XButton onClick={() => onDelete()} />;

    let fileIcon = null;

    if (attachmentType === 'pdf') {
      fileIcon = <FontAwesomeIcon icon={['far', 'file-pdf']} />;
    } else if (attachmentType === 'image') {
      fileIcon = <FontAwesomeIcon icon={['far', 'file-image']} />;
    } else if (attachmentType === 'audio') {
      fileIcon = <FontAwesomeIcon icon={['far', 'file-audio']} />;
    } else if (attachmentType === 'website') {
      fileIcon = <FontAwesomeIcon icon={['far', 'globe']} />;
    } else if (attachmentType === 'text') {
      fileIcon = <FontAwesomeIcon icon={['far', 'file-alt']} />;
    } else if (attachmentType === 'video') {
      fileIcon = <FontAwesomeIcon icon={['far', 'video']} />;
    }
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
        {fileIcon} <span style={{ padding: '4px' }}>Example {index}</span> {deleteBtn}
      </div>
    );
  };

  const renderDataPreview = (attachment: string, attachmentType: string) => {
    if (!attachment || !attachmentType) {
      return <div className={classes.text}>Error loading example.</div>;
    }

    // assume unprocessed is image for now
    if (attachmentType === 'image' || attachmentType === 'unprocessed') {
      return <img className={classes.img} src={attachment} crossOrigin="anonymous" />;
    } else if (attachmentType === 'text') {
      return <div className={classes.text}>{attachment}</div>;
    } else if (attachmentType === 'pdf') {
      return <object className={classes.pdf} data={attachment}></object>;
    } else if (attachmentType === 'audio' || attachmentType === 'transcription_audio') {
      return <audio className={classes.audio} src={attachment} controls />;
    } else if (attachmentType === 'website') {
      return <div className={classes.website}>{attachment}</div>;
    } else if (attachmentType === 'video') {
      return <video className={classes.video} src={attachment}></video>;
    } else {
      return null;
    }
  };

  return (
    <div className={classes.label}>
      {exampleViewerOpen && (
        <ExampleViewer
          modalOpen={exampleViewerOpen}
          lastSelection={curSelection}
          setModalOpen={arg => setExampleViewerOpen(arg)}
          taskId={taskId}
          location={location}
          taskInfo={{
            ...taskData,
            goodExample: isGoodExample,
            caption,
          }}
          readOnly={readOnly}
          hasOpenedExample={hasOpenedExample}
          projectTemplateId={projectTemplateId}
          handleUpdateExample={handleUpdateExample}
        />
      )}
      {
        <div>
          <div
            onClick={async e => {
              e.preventDefault();
              setCurSelection(editor.selection);
              setExampleViewerOpen(true);
            }}
          >
            <div>{renderLabel(index + 1)}</div>

            <div className={classes.preview} style={{ border: `2px solid ${borderColor}` }}>
              {renderDataPreview(attachment, attachmentType)}
            </div>
          </div>
        </div>
      }
    </div>
  );
};

const useStyles = makeStyles({
  label: {
    '&:hover $button': {
      opacity: 1,
    },
    position: 'relative',
  },
  button: {
    opacity: '0',
    top: '-6px',
    right: '-6px',
    position: 'absolute',
    zIndex: 1,
    color: 'gray',
  },
  preview: {
    width: '120px',
    height: '120px',
    position: 'relative',
    overflow: 'hidden',
  },
  img: {
    objectFit: 'cover',
    width: '120px',
    height: '120px',
  },
  pdf: {
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    pointerEvents: 'none',
  },
  audio: {
    width: '100%',
    height: '100%',
    background: '#dddddd',
  },
  video: {
    width: '100%',
    height: '100%',
    background: '#dddddd',
    padding: '2px',
    boxSizing: 'border-box',
  },
  website: {
    background: '#dddddd',
    fontSize: '10px',
    overflow: 'hidden',
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    padding: '4px',
  },
  text: {
    background: '#dddddd',
    fontSize: '10px',
    overflow: 'hidden',
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
    padding: '4px',
    maxHeight: '120px',
  },
});
